import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";

const AdjustmentsSection = ({ adjustments = [], isLocked, yearMonth, canonicalTutorId }) => {
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", description: "" });
  const [addForm, setAddForm] = useState({ amount: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["payrollLineItems", yearMonth, canonicalTutorId] });
    queryClient.invalidateQueries({ queryKey: ["payrollTutors", yearMonth] });
    queryClient.invalidateQueries({ queryKey: ["payrollRun", yearMonth] });
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({ amount: item.amount.toString(), description: item.description || "" });
    setError(null);
  };

  const cancelEdit = () => { setEditId(null); setError(null); };

  const saveEdit = async () => {
    const amt = parseFloat(editForm.amount);
    if (isNaN(amt)) { setError("Enter a valid amount."); return; }
    if (!editForm.description.trim()) { setError("Description is required."); return; }
    setSaving(true); setError(null);
    try {
      await apiService.updateAdjustment(yearMonth, editId, amt, editForm.description.trim());
      setEditId(null);
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to update adjustment.");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    setSaving(true); setError(null);
    try {
      await apiService.deleteAdjustment(yearMonth, id);
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to delete adjustment.");
    } finally {
      setSaving(false);
    }
  };

  const addAdjustment = async () => {
    const amt = parseFloat(addForm.amount);
    if (isNaN(amt)) { setError("Enter a valid amount."); return; }
    if (!addForm.description.trim()) { setError("Description is required."); return; }
    setSaving(true); setError(null);
    try {
      await apiService.createAdjustment(yearMonth, canonicalTutorId, amt, addForm.description.trim());
      setAddForm({ amount: "", description: "" });
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to create adjustment.");
    } finally {
      setSaving(false);
    }
  };

  const s = {
    card: { background: "white", borderRadius: "0.5rem", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)", padding: "1.5rem", border: "1px solid #e2e8f0", marginBottom: "1.5rem" },
    cardTitle: { fontSize: "1.3rem", fontWeight: "600", color: "#001272", margin: "0 0 0.75rem 0" },
    item: { backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "0.375rem", padding: "0.75rem", marginBottom: "0.5rem", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" },
    itemHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
    amount: (v) => ({ fontWeight: "600", fontSize: "1.05rem", color: v < 0 ? "#dc2626" : "#059669" }),
    label: { fontSize: "0.75rem", color: "#64748b", fontWeight: "500", marginBottom: "0.1rem" },
    desc: { fontSize: "0.85rem", color: "#4b5563" },
    chip: { display: "inline-block", fontSize: "0.7rem", fontWeight: "600", padding: "0.1rem 0.45rem", borderRadius: "9999px", background: "#f3e8ff", color: "#6b21a8" },
    actionBtn: (color) => ({ background: "none", border: "none", cursor: "pointer", fontSize: "0.78rem", fontWeight: "500", color, padding: "0.1rem 0.3rem" }),
    input: { border: "1px solid #cbd5e1", borderRadius: "0.25rem", padding: "0.4rem 0.6rem", fontSize: "0.875rem", width: "100%", boxSizing: "border-box" },
    btn: (color, bg) => ({ backgroundColor: bg, color, border: "none", borderRadius: "0.25rem", padding: "0.4rem 0.9rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer" }),
    formGrid: { display: "grid", gridTemplateColumns: "120px 1fr auto auto", gap: "0.5rem", alignItems: "end", marginTop: "1rem" },
    errorMsg: { color: "#dc2626", fontSize: "0.8rem", marginTop: "0.5rem" },
    empty: { color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.75rem" },
  };

  const renderViewItem = (item) => (
    <div key={item.id} style={s.item}>
      <div style={s.itemHeader}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.2rem" }}>
            <span style={s.chip}>Adjustment</span>
          </div>
          <div style={s.desc}>{item.description}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.25rem" }}>
          <div style={s.amount(item.amount)}>{formatCurrency(item.amount)}</div>
          {!isLocked && (
            <div>
              <button style={s.actionBtn("#0369a1")} onClick={() => startEdit(item)} disabled={saving}>Edit</button>
              <button style={s.actionBtn("#dc2626")} onClick={() => deleteItem(item.id)} disabled={saving}>Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEditItem = (item) => (
    <div key={item.id} style={{ ...s.item, borderColor: "#bfdbfe", backgroundColor: "#f0f9ff" }}>
      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <div>
          <div style={s.label}>Amount (USD)</div>
          <input
            style={s.input}
            type="number"
            step="0.01"
            value={editForm.amount}
            onChange={(e) => setEditForm(f => ({ ...f, amount: e.target.value }))}
            placeholder="e.g. -25.00"
          />
        </div>
        <div>
          <div style={s.label}>Description</div>
          <input
            style={s.input}
            type="text"
            value={editForm.description}
            onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Reason for adjustment"
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button style={s.btn("white", "#001272")} onClick={saveEdit} disabled={saving}>Save</button>
        <button style={s.btn("#4b5563", "#f1f5f9")} onClick={cancelEdit} disabled={saving}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div style={s.card}>
      <h2 style={s.cardTitle}>Adjustments</h2>

      {adjustments.length === 0 && (
        <div style={s.empty}>No manual adjustments for this tutor.</div>
      )}

      {adjustments.map(item => editId === item.id ? renderEditItem(item) : renderViewItem(item))}

      {error && <div style={s.errorMsg}>{error}</div>}

      {!isLocked && (
        <div style={{ marginTop: adjustments.length > 0 ? "1rem" : 0, paddingTop: adjustments.length > 0 ? "1rem" : 0, borderTop: adjustments.length > 0 ? "1px solid #e2e8f0" : "none" }}>
          <div style={{ fontSize: "0.9rem", fontWeight: "600", color: "#001272", marginBottom: "0.5rem" }}>Add Adjustment</div>
          <div style={s.formGrid}>
            <div>
              <div style={s.label}>Amount (USD)</div>
              <input
                style={s.input}
                type="number"
                step="0.01"
                value={addForm.amount}
                onChange={(e) => setAddForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="e.g. -25.00"
              />
            </div>
            <div>
              <div style={s.label}>Description (required)</div>
              <input
                style={s.input}
                type="text"
                value={addForm.description}
                onChange={(e) => setAddForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Reason for adjustment"
              />
            </div>
            <div style={{ paddingBottom: "0.05rem" }}>
              <button
                style={{ ...s.btn("white", "#001272"), whiteSpace: "nowrap" }}
                onClick={addAdjustment}
                disabled={saving}
              >
                {saving ? "Saving…" : "Add"}
              </button>
            </div>
            <div />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdjustmentsSection;
